import React, { useEffect, useState, useMemo, useContext } from "react";
import { supabase } from "lib/supabaseClient";
import { getAllLogs, insertLog } from "services/logService";
import { UserContext } from "layouts/admin";
import {
  MdShield, MdSearch, MdRefresh, MdPrint, MdDelete,
  MdPerson, MdLogin, MdEdit, MdFlightTakeoff, MdArticle,
  MdFilterList,
} from "react-icons/md";

// ─── Super admins who can delete logs ──────────────────────
const SUPER_ADMIN_IDS = [
  "6f226d92-8217-415b-991a-69da541ed483", // Abdulaziz
  "dea0edd5-c02f-4203-bfe2-c57f9b10d965", // Muath
  "00c3e1ce-6328-4ac2-864e-f636884b1234", // حساب للتجارب
];

const ACTION_META = {
  LOGIN_SUCCESS:        { color: "#10b981", icon: <MdLogin />,         label: "Login" },
  LOGIN_FAILED:         { color: "#ef4444", icon: <MdLogin />,         label: "Failed Login" },
  AUTO_LOGOUT:          { color: "#f97316", icon: <MdLogin />,         label: "Auto Logout" },
  ADD_EMPLOYEE:         { color: "#3201e9", icon: <MdPerson />,        label: "New Employee" },
  DELETE_EMPLOYEE:      { color: "#ef4444", icon: <MdDelete />,        label: "Delete Employee" },
  RESET_PASSWORD:       { color: "#f97316", icon: <MdShield />,        label: "Reset Password" },
  PROFILE_UPDATED:      { color: "#8b5cf6", icon: <MdEdit />,          label: "Profile Updated" },
  LEAVE_APPROVED:       { color: "#10b981", icon: <MdFlightTakeoff />, label: "Leave Approved" },
  LEAVE_REJECTED:       { color: "#ef4444", icon: <MdFlightTakeoff />, label: "Leave Rejected" },
  LEAVE_DELETED:        { color: "#f97316", icon: <MdDelete />,        label: "Leave Deleted" },
  LEAVE_AUTHORIZED:     { color: "#10b981", icon: <MdFlightTakeoff />, label: "Leave Authorized" },
  LEAVE_CANCELLED:      { color: "#f97316", icon: <MdFlightTakeoff />, label: "Leave Cancelled" },
  CLEAR_LEAVE_HISTORY:  { color: "#ef4444", icon: <MdDelete />,        label: "History Wiped" },
  ARTICLE_CREATED:      { color: "#3201e9", icon: <MdArticle />,       label: "Article Created" },
  ARTICLE_UPDATED:      { color: "#8b5cf6", icon: <MdArticle />,       label: "Article Updated" },
  ARTICLE_DELETED:      { color: "#ef4444", icon: <MdArticle />,       label: "Article Deleted" },
  ARTICLE_PUBLISHED:    { color: "#10b981", icon: <MdArticle />,       label: "Published" },
  ARTICLE_UNPUBLISHED:  { color: "#f97316", icon: <MdArticle />,       label: "Unpublished" },
  FILE_UPLOADED:        { color: "#3201e9", icon: <MdShield />,        label: "File Uploaded" },
  FILE_DELETED:         { color: "#ef4444", icon: <MdDelete />,        label: "File Deleted" },
};

const getMeta = (action) => ACTION_META[action] || { color: "#6b7280", icon: <MdShield />, label: action };

const fmt = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleDateString("en-GB") + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

export default function SecurityLogs() {
  const { userProfile } = useContext(UserContext);
  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [filter,      setFilter]      = useState("ALL");
  const [deleting,    setDeleting]    = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // ── Get real user ID directly from Supabase auth ──
  useEffect(() => {
    // استخدم الـ session مباشرة بدل getUser
    supabase.auth.getSession().then(({ data: { session } }) => {
      const uid = session?.user?.id;
      if (uid) {
        setCurrentUserId(uid);
        setIsSuperAdmin(SUPER_ADMIN_IDS.includes(uid));
      }
    });
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try { setLogs((await getAllLogs()) || []); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, []);

  // Realtime
  useEffect(() => {
    const ch = supabase.channel("security-logs-rt")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "security_logs" }, fetchLogs)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, []);

  // ── Delete all logs (super admin only) ──
  const handleDeleteAll = async () => {
    if (!isSuperAdmin) return;
    if (!window.confirm("Permanently delete ALL security logs? This cannot be undone.")) return;
    setDeleting(true);
    try {
      // 1. احذف كل الـ logs بدون استثناء
      const { error } = await supabase.rpc("delete_all_security_logs");
      
      if (error) {
        // fallback: احذف بـ timestamp قديم جداً (يشمل كل شيء)
        const futureDate = new Date();
        futureDate.setFullYear(futureDate.getFullYear() + 100);
        await supabase
          .from("security_logs")
          .delete()
          .lte("timestamp", futureDate.toISOString());
      }

      // 2. صفّر الـ state فوراً بدون انتظار fetch
      setLogs([]);

      // 3. سجّل الحذف (بدون await حتى ما تظهر في القائمة الفاضية)
      insertLog({
        timestamp:    new Date().toISOString(),
        actor_name:   userProfile?.full_name || "Super Admin",
        actor_number: userProfile?.employee_number || "—",
        actor_role:   userProfile?.role || "admin",
        action:       "LOGS_WIPED",
        details:      "All security logs permanently deleted by super admin",
      }).then(() => {
        // بعد ما يتسجل الـ log الجديد، اجلب القائمة
        fetchLogs();
      });

    } catch (err) { alert(err.message); }
    finally { setDeleting(false); }
  };

  // ── Print ──
  const handlePrint = () => {
    const rows = filtered.map(l => {
      const meta = getMeta(l.action);
      return `<tr>
        <td>${fmt(l.timestamp)}</td>
        <td>${l.actor_name || "—"} <br><small style="color:#999">#${l.actor_number || "—"} · ${l.actor_role || "—"}</small></td>
        <td><span style="color:${meta.color};font-weight:bold;">${meta.label}</span></td>
        <td>${l.target_name || "—"}</td>
        <td style="font-size:10px;color:#666;">${l.details || "—"}</td>
      </tr>`;
    }).join("");

    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Security Audit Log — CORE</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:'Segoe UI',Arial,sans-serif;color:#111;padding:32px;font-size:11px;}
        .header{border-bottom:2px solid #3201e9;padding-bottom:16px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:flex-end;}
        h1{font-size:20px;font-weight:300;letter-spacing:3px;text-transform:uppercase;color:#3201e9;}
        .meta{font-size:10px;color:#999;text-align:right;line-height:1.8;}
        table{width:100%;border-collapse:collapse;}
        thead tr{background:#3201e9;color:#fff;}
        th{padding:8px 12px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;}
        td{padding:8px 12px;border-bottom:1px solid #f0f0f0;vertical-align:top;}
        tr:nth-child(even) td{background:#f8f9ff;}
        .footer{margin-top:24px;font-size:9px;color:#ccc;display:flex;justify-content:space-between;}
        @media print{body{padding:16px;}}
      </style></head><body>
      <div class="header">
        <div><h1>CORE Professional</h1><div style="font-size:11px;color:#666;margin-top:4px;letter-spacing:1px;">SECURITY AUDIT LOG</div></div>
        <div class="meta">
          Generated: ${new Date().toLocaleDateString("en-GB")}<br>
          Time: ${new Date().toLocaleTimeString("en-GB")}<br>
          Total: ${filtered.length} records<br>
          By: ${userProfile?.full_name || "Admin"}
        </div>
      </div>
      <table>
        <thead><tr><th>Timestamp</th><th>Actor</th><th>Action</th><th>Target</th><th>Details</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">
        <span>CORE Professional — Security Audit System</span>
        <span>Exported: ${new Date().toISOString()}</span>
      </div>
      <script>window.onload=()=>window.print()<\/script>
      </body></html>`);
    win.document.close();
  };

  const categories = ["ALL", "LOGIN", "EMPLOYEE", "LEAVE", "ARTICLE", "OTHER"];

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return logs.filter(l => {
      const matchSearch = !term ||
        l.actor_name?.toLowerCase().includes(term) ||
        l.actor_number?.toLowerCase().includes(term) ||
        l.action?.toLowerCase().includes(term) ||
        l.target_name?.toLowerCase().includes(term) ||
        l.details?.toLowerCase().includes(term);

      const a = l.action || "";
      const matchFilter = filter === "ALL"
        || (filter === "LOGIN"    && a.includes("LOGIN") || a.includes("LOGOUT"))
        || (filter === "EMPLOYEE" && (a.includes("EMPLOYEE") || a.includes("PASSWORD") || a.includes("PROFILE")))
        || (filter === "LEAVE"    && a.includes("LEAVE"))
        || (filter === "ARTICLE"  && a.includes("ARTICLE"))
        || (filter === "OTHER"    && !["LOGIN","LOGOUT","EMPLOYEE","PASSWORD","PROFILE","LEAVE","ARTICLE"].some(k => a.includes(k)));

      return matchSearch && matchFilter;
    });
  }, [logs, search, filter]);

  return (
    <div className="min-h-screen bg-white p-4 md:p-8 font-sans">

      {/* Header */}
      <div className="mb-10 flex flex-col md:flex-row items-center justify-between gap-4 border-b border-gray-200 pb-8">
        <div>
          <h1 className="text-3xl font-medium uppercase italic tracking-tighter text-core-charcoal">
            CORE <span className="text-core-accent">Security</span>
          </h1>
          <p className="mt-2 text-[10px] uppercase tracking-[0.4em] text-gray-500 font-medium">
            Full Audit Trail • {logs.length} total records
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchLogs} title="Refresh"
            className="rounded-2xl border border-gray-200 bg-white p-4 text-gray-500 hover:text-core-accent hover:shadow-md transition-all">
            <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={handlePrint}
            className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-6 py-4 text-[11px] uppercase text-gray-500 hover:text-core-charcoal hover:shadow-md transition-all">
            <MdPrint size={18} /> Export PDF
          </button>
          {isSuperAdmin && (
            <button onClick={handleDeleteAll} disabled={deleting}
              className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-6 py-4 text-[11px] uppercase text-red-500 hover:bg-red-500 hover:text-white transition-all disabled:opacity-50">
              <MdDelete size={18} /> {deleting ? "Deleting..." : "Clear All Logs"}
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Events",  value: logs.length,                                                               color: "#3201e9" },
          { label: "Logins",        value: logs.filter(l => l.action?.includes("LOGIN")).length,                      color: "#10b981" },
          { label: "Leave Actions", value: logs.filter(l => l.action?.includes("LEAVE")).length,                      color: "#f97316" },
          { label: "Failed Logins", value: logs.filter(l => l.action === "LOGIN_FAILED").length,                      color: "#ef4444" },
        ].map(s => (
          <div key={s.label} className="rounded-[24px] border border-gray-200 bg-white p-6 shadow-sm">
            <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500">{s.label}</p>
            <p className="mt-2 text-3xl font-extralight italic" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, ID, action, details..."
            className="w-full rounded-2xl border border-gray-200 bg-white py-4 pl-12 pr-4 text-sm text-core-charcoal outline-none focus:border-core-accent transition-all placeholder:text-gray-400 shadow-sm" />
        </div>
        <div className="flex gap-1 rounded-2xl border border-gray-200 bg-white p-1 flex-wrap shadow-sm">
          {categories.map(c => (
            <button key={c} onClick={() => setFilter(c)}
              className={`rounded-xl px-4 py-2 text-[10px] uppercase tracking-wider transition-all ${filter === c ? "bg-core-accent text-white shadow-md" : "text-gray-500 hover:text-core-charcoal hover:bg-gray-50"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-[32px] border border-gray-200 bg-white overflow-hidden shadow-sm">
        {loading ? (
          <div className="py-20 text-center text-gray-400 uppercase tracking-widest text-sm font-medium animate-pulse">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 text-[10px] uppercase tracking-[0.2em] text-core-charcoal bg-white font-medium">
                  <th className="px-6 py-5 text-left">Timestamp</th>
                  <th className="px-6 py-5 text-left">Actor</th>
                  <th className="px-6 py-5 text-left">Action</th>
                  <th className="px-6 py-5 text-left">Target</th>
                  <th className="px-6 py-5 text-left">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((log, i) => {
                  const meta = getMeta(log.action);
                  return (
                    <tr key={log.id || i} className="group hover:bg-gray-50 transition-all">
                      <td className="px-6 py-5 text-[11px] text-gray-500 font-mono font-medium whitespace-nowrap">
                        {fmt(log.timestamp)}
                      </td>
                      <td className="px-6 py-5">
                        <p className="text-[12px] font-medium text-core-charcoal">{log.actor_name || "—"}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-[9px] text-gray-400 uppercase font-medium">#{log.actor_number || "—"}</span>
                          {log.actor_role && (
                            <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-[8px] uppercase font-medium text-blue-600">
                              {log.actor_role}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <div className="rounded-lg p-1.5" style={{ backgroundColor: meta.color + "20", color: meta.color }}>
                            {React.cloneElement(meta.icon, { size: 14 })}
                          </div>
                          <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: meta.color }}>
                            {meta.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        {log.target_name ? (
                          <div>
                            <p className="text-[11px] text-gray-600">{log.target_name}</p>
                            {log.target_number && <p className="text-[9px] text-gray-400">#{log.target_number}</p>}
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-5 max-w-xs">
                        <p className="text-[11px] text-gray-500 italic leading-relaxed line-clamp-2">{log.details || "—"}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-20 text-center text-gray-400 uppercase tracking-widest text-sm">No logs found</div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 text-center text-[10px] text-gray-400 uppercase tracking-widest">
        Showing {filtered.length} of {logs.length} records
        {isSuperAdmin && <span className="ml-4 text-orange-500/60">· Super Admin Mode</span>}
      </div>
    </div>
  );
}